// src/controllers/RegistrationController.js - UPDATED with password integration
import { BusinessProfile } from '../models/BusinessProfile';
import { Service } from '../models/Service';
import { User } from '../models/User';
import { ValidationService } from '../services/ValidationService';

export class RegistrationController {
  constructor() {
    this.validationErrors = {};
    this.isProcessing = false;
  }

  // Bridge method: Convert existing RegistrationFlow formData to models
  validateRegistrationData(formData) {
    console.log('🔄 Converting existing formData to models...', formData);
    
    try {
      // Create business profile from existing formData structure
      const businessProfile = BusinessProfile.fromRegistrationData(formData);
      const businessValidation = businessProfile.validate();
      
      // Create user from existing formData structure  
      const user = User.fromRegistrationData(formData);
      const userValidation = user.validate();
      
      // UPDATED: Validate password requirements
      const passwordErrors = this.validatePasswordRequirements(formData);
      
      // Convert existing services to Service models
      const services = [];
      const serviceValidationErrors = [];
      
      Object.entries(formData.services || {}).forEach(([serviceKey, serviceData]) => {
        try {
          const service = Service.fromRegistrationService(serviceKey, serviceData, formData.phoneNumber);
          const serviceValidation = service.validate();
          
          if (serviceValidation.isValid) {
            services.push(service);
          } else {
            serviceValidationErrors.push(...serviceValidation.errors);
          }
        } catch (error) {
          serviceValidationErrors.push(`Error processing service ${serviceKey}: ${error.message}`);
        }
      });

      // Aggregate all validation results
      const allErrors = [
        ...businessValidation.errors,
        ...userValidation.errors,
        ...passwordErrors,
        ...serviceValidationErrors
      ];

      const isValid = allErrors.length === 0;

      console.log('✅ Registration validation complete:', {
        isValid,
        businessProfile: businessProfile.toPublicProfile(),
        user: user.toPublicProfile(),
        services: services.map(s => s.toPublicData()),
        errors: allErrors
      });

      return {
        isValid,
        errors: allErrors,
        models: {
          businessProfile,
          user,
          services
        }
      };

    } catch (error) {
      console.error('❌ Registration validation failed:', error);
      return {
        isValid: false,
        errors: [`Registration validation failed: ${error.message}`],
        models: null
      };
    }
  }

  // NEW: Validate password requirements
  validatePasswordRequirements(formData) {
    const errors = [];
    const { password, confirmPassword } = formData;
    
    if (!password) {
      errors.push('Password is required');
      return errors;
    }
    
    if (password.length < 8) {
      errors.push('Password must be at least 8 characters long');
    }
    
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least one uppercase letter');
    }
    
    if (!/[a-z]/.test(password)) {
      errors.push('Password must contain at least one lowercase letter');
    }
    
    if (!/\d/.test(password)) {
      errors.push('Password must contain at least one number');
    }
    
    if (password !== confirmPassword) {
      errors.push('Passwords do not match');
    }
    
    return errors;
  }

  // UPDATED: Prepare data for Firebase (includes password)
  prepareFirebaseData(validatedModels, formData) {
    const { businessProfile, user, services } = validatedModels;
    
    // Convert to existing Firebase structure that your AI systems expect
    const firebaseData = {
      // ✅ EXISTING STRUCTURE (PRESERVED for AI compatibility)
      appointment_duration_minutes: services.reduce((acc, service) => {
        // Use the original service key mapping
        const keyMap = {
          'Haircut': 'haircut',
          'Beard Trim': 'beard_trim', 
          'Full Service': 'full_service',
          'Consultation': 'consultation',
          'Hair Color': 'color',
          'Hair Styling': 'styling',
          'Manicure': 'manicure',
          'Pedicure': 'pedicure',
          'Gel Polish': 'gel_polish',
          'Massage': 'massage',
          'Facial': 'facial',
          'Body Treatment': 'body_treatment'
        };
        
        const key = keyMap[service.name] || service.name.toLowerCase().replace(/\s+/g, '_');
        acc[key] = String(service.duration);
        return acc;
      }, {}),
      
      formatted_address: businessProfile.formattedAddress,
      tier: businessProfile.tier,
      timezone: businessProfile.timezone,
      whatsapp_number: businessProfile.whatsappNumber,
      'working-hours': businessProfile.workingHours,
      number_of_chairs: businessProfile.numberOfChairs,
      business_name: businessProfile.businessName,
      business_type: businessProfile.businessType,
      owner_name: businessProfile.ownerName,
      owner_email: businessProfile.ownerEmail,
      billing_cycle: businessProfile.billingCycle,
      
      // 🆕 NEW STRUCTURE (ADDITIONS - won't break existing AI)
      services: services.reduce((acc, service) => {
        acc[service.id] = service.toFirebaseData();
        return acc;
      }, {}),
      
      settings: businessProfile.settings,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      status: 'active'
    };

    // UPDATED: Include password in user data
    const userData = {
      email: user.email,
      displayName: user.displayName,
      phoneNumber: user.phoneNumber,
      role: user.role,
      permissions: user.permissions,
      businessId: user.businessId,
      password: formData.password  // ✅ Now includes the real password
    };

    console.log('🔄 Prepared Firebase data:', { firebaseData, userData: { ...userData, password: '[REDACTED]' } });
    
    return { firebaseData, userData };
  }

  // Method to enhance existing services during registration
  enhanceExistingServices(formData) {
    const businessType = formData.businessType;
    const currentServices = Object.keys(formData.services || {});
    
    // Get suggested additional services
    const existingServiceObjects = Object.entries(formData.services || {}).map(([key, data]) => ({
      name: key.replace('_', ' '),
      duration: data.duration,
      price: data.price
    }));
    
    const suggestions = Service.getSuggestedServices(businessType, existingServiceObjects);
    
    // Get all available categories for this business type
    const allCategories = Service.getAllServiceCategories();
    const relevantCategories = this.getRelevantCategories(businessType);
    
    return {
      currentServices: existingServiceObjects,
      suggestedServices: suggestions,
      availableCategories: allCategories.filter(cat => 
        relevantCategories.includes(cat.id)
      ),
      popularServicesByCategory: relevantCategories.reduce((acc, category) => {
        acc[category] = Service.getPopularServicesByCategory(category);
        return acc;
      }, {})
    };
  }

  getRelevantCategories(businessType) {
    const categoryMap = {
      barbershop: ['haircut', 'beard', 'styling', 'consultation'],
      hair_salon: ['haircut', 'coloring', 'styling', 'treatment', 'consultation'],
      nail_salon: ['nails', 'waxing', 'consultation'],
      spa: ['massage', 'facial', 'treatment', 'waxing', 'consultation'],
      beauty_studio: ['makeup', 'facial', 'waxing', 'nails', 'consultation']
    };
    
    return categoryMap[businessType] || ['general', 'consultation'];
  }

  // UPDATED: Real-time validation for UI (includes password validation)
  validateStep(stepNumber, formData) {
    switch (stepNumber) {
      case 2: // Business Details
        return this.validateBusinessDetails(formData);
      case 3: // Services
        return this.validateServices(formData);
      case 4: // Working Hours
        return this.validateWorkingHours(formData);
      case 6: // Final (includes password validation)
        return this.validateFinalStep(formData);
      default:
        return { isValid: true, errors: [] };
    }
  }

  validateBusinessDetails(formData) {
    const errors = [];
    
    if (!ValidationService.isValidBusinessName(formData.businessName)) {
      errors.push('Business name must be at least 2 characters');
    }
    
    if (!ValidationService.isValidPhoneNumber(formData.phoneNumber)) {
      errors.push('Valid phone number is required');
    }
    
    if (!ValidationService.isValidAddress(formData.address)) {
      errors.push('Valid business address is required');
    }
    
    if (!formData.businessType) {
      errors.push('Please select a business type');
    }

    return { isValid: errors.length === 0, errors };
  }

  validateServices(formData) {
    const servicesValidation = ValidationService.validateServiceDurations(formData.services);
    return servicesValidation;
  }

  validateWorkingHours(formData) {
    const hoursValidation = ValidationService.validateWorkingHours(formData.workingHours);
    return hoursValidation;
  }

  // NEW: Validate final step including password
  validateFinalStep(formData) {
    const errors = [];
    
    if (!formData.ownerName || formData.ownerName.trim().length < 2) {
      errors.push('Your name is required');
    }
    
    if (!ValidationService.isValidEmail(formData.ownerEmail)) {
      errors.push('Valid email address is required');
    }
    
    // Password validation
    const passwordErrors = this.validatePasswordRequirements(formData);
    errors.push(...passwordErrors);
    
    return { isValid: errors.length === 0, errors };
  }

  // Helper method to get enhanced service suggestions for Step 3
  getServiceEnhancements(formData) {
    if (!formData.businessType) {
      return { suggestions: [], categories: [] };
    }

    return this.enhanceExistingServices(formData);
  }

  // Method to add custom service during registration
  addCustomService(formData, customServiceData) {
    try {
      // Create new service model
      const service = Service.createCustomService(formData.phoneNumber, customServiceData);
      const validation = service.validate();
      
      if (!validation.isValid) {
        return { success: false, errors: validation.errors };
      }

      // Add to existing formData structure for compatibility
      const serviceKey = customServiceData.name.toLowerCase().replace(/\s+/g, '_');
      const updatedFormData = {
        ...formData,
        services: {
          ...formData.services,
          [serviceKey]: {
            duration: service.duration.toString(),
            price: service.price.toString()
          }
        }
      };

      return { 
        success: true, 
        updatedFormData, 
        service: service.toPublicData() 
      };
    } catch (error) {
      return { 
        success: false, 
        errors: [`Failed to add custom service: ${error.message}`] 
      };
    }
  }
}
